import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ImagePlus, Trophy, ZoomIn, X } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

// Letters A, B, C… used to label the answer options.
export const LETTER = (i) => String.fromCharCode(65 + i);

const NmtImageElement = ({
  element,
  isEditor = false,
  studentView = false,
}) => {
  const { t } = useTranslation();
  const { content } = element;
  const {
    questions = [],
    options = [],
    fontFamily = 'Evolventa',
    fontSize = 16,
    textColor = '#1f2937',
    buttonColor = '#2A4D25',
  } = content || {};

  const validQuestions = useMemo(
    () => (questions || []).filter((q) => q && q.imageUrl),
    [questions]
  );

  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null); // option id currently selected
  const [revealed, setRevealed] = useState(false);
  const [results, setResults] = useState([]); // { questionId, picked, correct }
  const [zoomedUrl, setZoomedUrl] = useState(null);
  const optionRowRef = useRef(null);

  // Reset when the underlying questions/options change (editor edits, etc.).
  const sig = validQuestions.map((q) => q.id).join('|') + '#' + options.map((o) => o.id).join('|');
  useEffect(() => {
    setIdx(0);
    setPicked(null);
    setRevealed(false);
    setResults([]);
  }, [sig]);

  // Lightbox: close on ESC + lock scroll on both html and body while open.
  useEffect(() => {
    if (!zoomedUrl) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setZoomedUrl(null);
    };
    document.addEventListener('keydown', onKey);
    const htmlOverflow = document.documentElement.style.overflow;
    const bodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.style.overflow = htmlOverflow;
      document.body.style.overflow = bodyOverflow;
    };
  }, [zoomedUrl]);

  const currentQuestion = validQuestions[idx] || null;
  const done = !isEditor && validQuestions.length > 0 && idx >= validQuestions.length;

  const correctForCurrent = currentQuestion?.correctOptionId || null;
  const isCurrentCorrect = revealed && picked && picked === correctForCurrent;

  const handlePick = (optionId) => {
    if (isEditor || revealed || done) return;
    setPicked((prev) => (prev === optionId ? null : optionId));
  };

  const handleCheck = () => {
    if (!picked || revealed) return;
    setRevealed(true);
  };

  const handleShowAnswer = () => {
    if (revealed || !currentQuestion) return;
    setPicked(correctForCurrent);
    setRevealed(true);
  };

  const handleSkip = () => {
    if (!currentQuestion) return;
    setResults((prev) => [
      ...prev,
      { questionId: currentQuestion.id, picked: null, correct: false, skipped: true },
    ]);
    setPicked(null);
    setRevealed(false);
    setIdx((i) => i + 1);
  };

  const handleNext = () => {
    if (!currentQuestion) return;
    setResults((prev) => [
      ...prev,
      {
        questionId: currentQuestion.id,
        picked,
        correct: picked === correctForCurrent,
        skipped: false,
      },
    ]);
    setPicked(null);
    setRevealed(false);
    setIdx((i) => i + 1);
  };

  const handleRetry = () => {
    setIdx(0);
    setPicked(null);
    setRevealed(false);
    setResults([]);
  };

  // Empty state — editor without any images uploaded yet.
  if (!isEditor ? validQuestions.length === 0 : questions.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300">
        <ImagePlus size={40} className="text-gray-300 mb-2" />
        <p className="text-sm text-gray-400 px-4 text-center">
          {t('games.nmtImage.uploadInSettings')}
        </p>
      </div>
    );
  }

  const textStyle = {
    fontFamily,
    fontSize: `${fontSize}px`,
    color: textColor,
  };

  // Editor preview: mimic the first question so the teacher sees the layout.
  const previewQuestion = isEditor ? (validQuestions[0] || questions[0]) : currentQuestion;

  // Results screen
  if (done && !isEditor) {
    const correctCount = results.filter((r) => r.correct).length;
    const total = validQuestions.length;
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-6 rounded-xl bg-white">
        <Trophy size={44} style={{ color: buttonColor }} />
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-800" style={{ fontFamily }}>
            {t('games.nmtImage.resultTitle')}
          </p>
          <p className="mt-2 text-lg text-gray-600" style={{ fontFamily }}>
            {t('games.nmtImage.resultScore', { correct: correctCount, total })}
          </p>
        </div>
        <div className="w-full max-w-md grid grid-cols-8 gap-1.5">
          {results.map((r, i) => (
            <div
              key={i}
              className="aspect-square rounded-md flex items-center justify-center text-xs font-bold text-white"
              style={{ backgroundColor: r.correct ? '#22c55e' : (r.skipped ? '#9ca3af' : '#ef4444') }}
              title={`#${i + 1}: ${r.correct ? '✓' : (r.skipped ? '—' : '✗')}`}
            >
              {i + 1}
            </div>
          ))}
        </div>
        <button
          onClick={handleRetry}
          className="mt-2 px-5 py-2 rounded-lg text-white text-sm font-medium transition"
          style={{ backgroundColor: buttonColor }}
        >
          {t('games.common.tryAgain')}
        </button>
      </div>
    );
  }

  const q = previewQuestion;
  const showResultThisRound = revealed && !isEditor;

  return (
    <>
    <div className="w-full h-full flex flex-col rounded-xl bg-white overflow-hidden">
      {/* Progress + image */}
      <div className="flex-1 min-h-0 min-w-0 flex flex-col">
        <div className="px-4 pt-3 pb-1 flex items-center justify-between text-xs text-gray-500" style={{ fontFamily }}>
          <span>
            {isEditor
              ? t('games.nmtImage.imageCount', { count: questions.length })
              : t('games.nmtImage.progress', { current: idx + 1, total: validQuestions.length })}
          </span>
          {q?.prompt && (
            <span className="truncate ml-3 text-gray-600 font-medium">{q.prompt}</span>
          )}
        </div>

        <div className="flex-1 min-h-0 min-w-0 flex items-center justify-center px-4 py-3">
          {q?.imageUrl ? (
            <div className="relative max-w-full max-h-full flex items-center justify-center">
              <img
                src={getMediaUrl(q.imageUrl)}
                alt=""
                draggable={false}
                onClick={isEditor ? undefined : () => setZoomedUrl(getMediaUrl(q.imageUrl))}
                className={`max-w-full max-h-full object-contain select-none ${isEditor ? '' : 'cursor-zoom-in'}`}
              />
              {!isEditor && (
                <button
                  type="button"
                  onClick={() => setZoomedUrl(getMediaUrl(q.imageUrl))}
                  aria-label={t('games.nmtImage.zoom')}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition"
                >
                  <ZoomIn size={14} />
                </button>
              )}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg text-gray-400 text-sm">
              {t('games.nmtImage.noImage')}
            </div>
          )}
        </div>
      </div>

      {/* Options row (A B C … boxes) */}
      <div className="px-4 pb-2">
        <p className="text-xs text-gray-500 mb-2" style={{ fontFamily }}>
          {t('games.nmtImage.pickAnswer')}
        </p>
        <div
          ref={optionRowRef}
          className="grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${Math.max(1, options.length)}, minmax(0, 1fr))`,
          }}
        >
          {options.map((opt, i) => {
            const letter = opt.letter || LETTER(i);
            const isPicked = picked === opt.id;
            const isCorrectAnswer = opt.id === correctForCurrent;

            // Determine box styling
            let borderColor = '#d1d5db';
            let bgColor = '#ffffff';
            let markColor = '#374151';
            let ringClass = '';

            if (isEditor) {
              if (isCorrectAnswer) {
                borderColor = '#22c55e';
                bgColor = '#f0fdf4';
                markColor = '#15803d';
              }
            } else if (showResultThisRound) {
              if (isPicked && isCorrectAnswer) {
                borderColor = '#22c55e';
                bgColor = '#ffffff';
                markColor = '#22c55e';
              } else if (isPicked && !isCorrectAnswer) {
                borderColor = '#ef4444';
                bgColor = '#ffffff';
                markColor = '#ef4444';
              } else if (isCorrectAnswer) {
                borderColor = '#22c55e';
                bgColor = '#ffffff';
                markColor = '#22c55e';
              }
            } else if (isPicked) {
              borderColor = buttonColor;
              bgColor = '#ffffff';
              markColor = buttonColor;
              ringClass = 'ring-2';
            }

            return (
              <button
                key={opt.id || i}
                type="button"
                disabled={isEditor || showResultThisRound}
                onClick={() => handlePick(opt.id)}
                className="flex flex-col items-center gap-1 group"
                style={{ ...textStyle }}
              >
                <span className="text-[11px] font-bold tracking-wider" style={{ color: markColor }}>
                  {letter}
                </span>
                <span
                  className={`w-full aspect-square rounded-md border-2 flex items-center justify-center transition ${ringClass}`}
                  style={{
                    borderColor,
                    backgroundColor: bgColor,
                    ...(ringClass ? { boxShadow: `0 0 0 2px ${buttonColor}33` } : {}),
                  }}
                >
                  {(isPicked || (showResultThisRound && isCorrectAnswer)) && (
                    <svg viewBox="0 0 24 24" className="w-3/4 h-3/4" style={{ color: markColor }}>
                      <path
                        d="M4 4 L20 20 M20 4 L4 20"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        fill="none"
                      />
                    </svg>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Action buttons */}
      <div className="px-4 pb-4 pt-1 flex flex-col gap-2">
        {showResultThisRound ? (
          <button
            onClick={handleNext}
            className="w-full py-2.5 rounded-lg text-white text-sm font-medium transition hover:brightness-95"
            style={{ backgroundColor: buttonColor, fontFamily }}
          >
            {t('games.nmtImage.next')}
          </button>
        ) : picked ? (
          <button
            onClick={handleCheck}
            disabled={isEditor}
            className="w-full py-2.5 rounded-lg text-white text-sm font-medium transition hover:brightness-95 disabled:opacity-60"
            style={{ backgroundColor: buttonColor, fontFamily }}
          >
            {t('games.nmtImage.answer')}
          </button>
        ) : (
          <button
            onClick={handleSkip}
            disabled={isEditor}
            className="w-full py-2.5 rounded-lg text-white text-sm font-medium transition hover:brightness-95 disabled:opacity-60"
            style={{ backgroundColor: buttonColor, fontFamily }}
          >
            {t('games.nmtImage.skip')}
          </button>
        )}

        {!showResultThisRound && (
          <button
            onClick={handleShowAnswer}
            disabled={isEditor || !correctForCurrent}
            className="w-full py-2.5 rounded-lg text-white text-sm font-medium transition hover:brightness-95 disabled:opacity-60"
            style={{ backgroundColor: buttonColor, fontFamily }}
          >
            {t('games.nmtImage.showAnswer')}
          </button>
        )}
      </div>
    </div>

    {zoomedUrl && typeof document !== 'undefined' && createPortal(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4"
        onClick={() => setZoomedUrl(null)}
        role="dialog"
        aria-modal="true"
      >
        <img
          src={zoomedUrl}
          alt=""
          draggable={false}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full object-contain select-none cursor-zoom-out"
        />
        <button
          type="button"
          onClick={() => setZoomedUrl(null)}
          aria-label={t('games.nmtImage.closeZoom')}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
        >
          <X size={20} />
        </button>
      </div>,
      document.body
    )}
    </>
  );
};

export default NmtImageElement;
